import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import { useState } from 'react';

import { useAuth } from '@flipster/features/src/shared/auth/state';
import { calcIsolatedMarginAssigned } from '@flipster/features/src/shared/calculator/adjust-margin';
import {
  calcFlipEstimatedEntryPrice,
  calcFlipEstimatedMargin,
  calcFlipEstimatedPositionSize,
} from '@flipster/features/src/shared/calculator/instant-flip';
import { MovableModal } from '@flipster/features/src/shared/movable-modal';
import { useSnapshotSpec } from '@flipster/features/src/shared/snapshot/use-snapshot-spec';
import { FormattedMessage } from '@flipster/i18n';
import { pswapsBunja } from '@flipster/r230522/original/pswaps';
import { Checkbox } from '@flipster/react/design-system/checkbox';
import { SpinnerBox } from '@flipster/react/design-system/spinner';
import Formatted from '@flipster/react/design-utils/formatted';
import { css } from '@flipster/styled-system/css';
import { center, flex } from '@flipster/styled-system/patterns';
import ArrowDownIcon from '@flipster/ui/assets/svgs/unfilled/arrow-down.svg';
import { getQuoteCurrencyPrecision } from '@flipster/utils/constants/currency';
import { getFlipsterConfigsV1Key, setLocalStorage } from '@flipster/utils/localStorage';
import Num from '@flipster/utils/num';

import LossProtectedSection from '@web/components/LossProtected/LossProtectedSection';

import FlipSideCard from './FlipSideCard';
import { instantFlipModalBunja } from './state';
import { useInstantFlipModal } from './useInstantFlipModal';

interface Props {
  onFinish: () => void;
}

const InstantFlipContent = ({ onFinish }: Props) => {
  const t = useTranslations();
  const [isHideModal, setIsHideModal] = useState(false);

  const { user } = useAuth();
  const { instrumentsKeyBySymbol } = useSnapshotSpec();
  const { isFlipLoading, instantFlip } = useInstantFlipModal();
  const { currentPositionAtom } = useBunja(instantFlipModalBunja);
  const currentPosition = useAtomValue(currentPositionAtom);

  const { pswapsAtom } = useBunja(pswapsBunja);
  const pswaps = useAtomValue(pswapsAtom);

  if (!instrumentsKeyBySymbol) {
    return <SpinnerBox size={40} />;
  }

  const {
    side,
    slot,
    symbol,
    position,
    initLeverage,
    unrealizedPnl,
    calcUnrealizedPnlPercent,
    avgEntryPrice,
    marginType,
  } = currentPosition;
  const instrument = instrumentsKeyBySymbol[symbol];

  const takerFeeRate = pswaps?.[currentPosition.symbol]?.takerFeeRate ?? '0';
  const calculatedMargin = (() => {
    switch (currentPosition.marginType) {
      case 'Cross':
        return currentPosition.initPositionMargin;
      case 'Isolated':
        return calcIsolatedMarginAssigned(
          currentPosition.isolatedMarginInitial,
          currentPosition.isolatedMarginAdded,
          currentPosition.isolatedMarginRealizedFee,
        );
      default:
        // oxlint-disable-next-line no-unused-expressions
        currentPosition.marginType satisfies never;
        // oxlint-disable-next-line no-console
        console.error(`Unexpected margin type: ${currentPosition.marginType}`);
        return '0';
    }
  })();

  const flipSide = side === 'Long' ? 'Short' : 'Long';
  const flipEstimatedEntryPrice = calcFlipEstimatedEntryPrice(currentPosition);
  const flipEstimatedMargin = calcFlipEstimatedMargin(
    currentPosition,
    flipEstimatedEntryPrice,
    calculatedMargin,
    takerFeeRate,
  );
  const flipEstimatedChangeInFunds = Num(flipEstimatedMargin).minus(calculatedMargin).toString();
  const flipEstimatedPositionSize = calcFlipEstimatedPositionSize(
    currentPosition,
    flipEstimatedChangeInFunds,
    flipEstimatedEntryPrice,
    instrument.notionalMaxOrderAmount,
    instrument.unitOrderQty,
  );

  const getFlipButtonText = (side: 'Long' | 'Short') => {
    const key = side === 'Long' ? ('Flip_to_long_button' as const) : ('Flip_to_short_button' as const);
    return <FormattedMessage id={`Position_flip.r250704.${key}`} />;
  };

  return (
    <>
      <MovableModal.Content>
        <div className={flex({ direction: 'column', gap: 16 })}>
          <FlipSideCard
            side={side}
            quantity={position}
            currency={instrument.currency}
            pricePrecision={instrument.pricePrecision}
            quoteCurrency={instrument.quoteCurrency}
            unrealizedPnl={unrealizedPnl}
            calcUnrealizedPnlPercent={calcUnrealizedPnlPercent}
            entryPrice={avgEntryPrice}
            margin={calculatedMargin}
            leverage={initLeverage}
            marginType={marginType}
          />

          <section
            className={flex({
              position: 'relative',
              direction: 'column',
              align: 'center',
              justify: 'center',
              gap: 6,
              _before: {
                position: 'absolute',
                top: 14,
                right: 0,
                left: 0,
                height: 1,
                content: "''",
                background: 'background.default.level-4',
                opacity: 0.4,
              },
            })}
          >
            <div
              className={center({
                minWidth: 116,
                rounded: 70,
                gap: 4,
                p: 6,
                textStyle: 'Text/M/Strong',
                color: 'inverse.white',
                background: 'background.default.level-1',
                zIndex: 1,
              })}
            >
              <ArrowDownIcon />
              <span>{t('Position_flip.r250401.Flip_position')}</span>
            </div>
            <div
              className={center({
                gap: 4,
                textStyle: 'Text/L/Regular',
                color: 'content.default.level-5',
              })}
            >
              <span>{t('Position_flip.r250401.Est_change_in_funds')}</span>
              <Formatted.Number
                value={flipEstimatedChangeInFunds}
                precision={getQuoteCurrencyPrecision(instrument.quoteCurrency)}
                toFixed
                withColor
                withPlus
              />
            </div>
          </section>

          <FlipSideCard
            side={flipSide}
            quantity={flipEstimatedPositionSize}
            currency={instrument.currency}
            pricePrecision={instrument.pricePrecision}
            quoteCurrency={instrument.quoteCurrency}
            unrealizedPnl='0'
            entryPrice={flipEstimatedEntryPrice}
            margin={flipEstimatedMargin}
            leverage={initLeverage}
            marginType={marginType}
          />

          <p
            className={css({
              textStyle: 'Text/S/Regular',
              whiteSpace: 'pre-wrap',
              color: 'content.default.level-1',
            })}
          >
            <FormattedMessage id='Position_flip.r250912.Instant_flip_note_description' />
          </p>

          <LossProtectedSection css={css.raw({ pb: 16, borderBottom: '1px solid {colors.border.default.level-2}' })} />

          <Checkbox
            checked={isHideModal}
            onChange={() => setIsHideModal((prev) => !prev)}
            css={{ alignItems: 'center' }}
          >
            <FormattedMessage id='Position_flip.r250401.Dont_show_preview_again' />
          </Checkbox>
        </div>
      </MovableModal.Content>
      <MovableModal.Footer>
        <MovableModal.Button
          size='large'
          color={flipSide === 'Long' ? 'buy' : 'sell'}
          label={getFlipButtonText(flipSide)}
          isLoading={isFlipLoading}
          onClick={async () => {
            try {
              if (isHideModal && user) {
                const key = getFlipsterConfigsV1Key(user.userId, 'skipInstantFlipModal');
                setLocalStorage(key, true);
              }
              await instantFlip({ symbol, slot, side: flipSide, price: flipEstimatedEntryPrice });
            } finally {
              onFinish?.();
            }
          }}
        />
      </MovableModal.Footer>
    </>
  );
};

export default InstantFlipContent;
